import {
  CANCELLED_KEYS,
  HISTORY_KEYS,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-lifecycle';
import {
  type CoachingSession,
  type CoachProfile,
} from '@efdigital/ll-addon-data';
import { queryCoachProfilesByUserIds } from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import { queryCoachingSessionsByCoachingPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions';
import { excludeFromLearnerHistory } from '@efdigital/ll-xceed-v2/utils/coaching/session-current-categories';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

import type { CoachingCoacheeCohortModel } from '../../cohort';

export type CoachingCoacheeCohortSessionsPastModel =
  CoachingCoacheeCohortModel & {
    pastSessions: CoachingSession[];
    coaches: CoachProfile[];
  };

function sortByStartTimeDesc(sessions: CoachingSession[]): CoachingSession[] {
  return [...sessions].sort(
    (left, right) => Date.parse(right.startTime) - Date.parse(left.startTime)
  );
}

export default class CoachingCoacheeCohortSessionsPastRoute extends Route<CoachingCoacheeCohortSessionsPastModel> {
  @service declare store: Store;

  async model(): Promise<CoachingCoacheeCohortSessionsPastModel> {
    const cohortModel = this.modelFor(
      'coaching.coachee.cohort'
    ) as CoachingCoacheeCohortModel;
    const coachingPlanId = cohortModel.coachingPlan!.id!;

    const [cancelledSessions, historySessions] = await Promise.all([
      this.store.request(
        queryCoachingSessionsByCoachingPlan(coachingPlanId, CANCELLED_KEYS)
      ),
      this.store.request(
        queryCoachingSessionsByCoachingPlan(coachingPlanId, HISTORY_KEYS)
      ),
    ]);

    const cancelled = cancelledSessions.content.data;
    const history = historySessions.content.data.filter((session) =>
      excludeFromLearnerHistory(session, 'coachee')
    );
    const pastSessions = sortByStartTimeDesc([...cancelled, ...history]);

    const coachUserIds = Array.from(
      new Set(pastSessions.map((session) => session.coachUserId))
    );
    const coaches = await this.store.request(
      queryCoachProfilesByUserIds(coachUserIds)
    );

    return {
      ...cohortModel,
      pastSessions,
      coaches: coaches.content.data,
    };
  }
}
